// src/pages/LandingPage.tsx - HotelValuAI Landing Page
import React from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Fade,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeContext';
import {
  TrendingUp,
  Assessment,
  Security,
  Speed,
  Analytics,
  Apartment,
  BusinessCenter,
  StarOutline,
  CheckCircle,
  ArrowForward,
  PlayArrow,
  FormatQuote,
  Brightness4,
  Brightness7
} from '@mui/icons-material';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();

  const features = [
    {
      icon: Assessment,
      title: 'AI-Powered Valuation',
      description: 'Advanced machine learning algorithms analyze 500+ data points to deliver valuations with 98% accuracy, helping you make confident investment decisions.',
      color: theme.palette.primary.main,
      gradient: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    },
    {
      icon: TrendingUp,
      title: 'Market Intelligence',
      description: 'Real-time market insights and predictive analytics identify emerging trends and undervalued properties before your competition.',
      color: theme.palette.success.main,
      gradient: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
    },
    {
      icon: Analytics,
      title: 'Financial Analysis',
      description: 'Comprehensive financial modeling with stress testing, scenario analysis, and automated ROI calculations that reveal hidden profit potential.',
      color: theme.palette.info.main,
      gradient: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
    },
    {
      icon: Security,
      title: 'Enterprise Security',
      description: 'Bank-level encryption, SOC 2 compliance, and secure cloud infrastructure protect your sensitive investment data and analysis.',
      color: theme.palette.warning.main,
      gradient: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
    },
    {
      icon: Speed,
      title: 'Lightning Fast',
      description: 'Generate comprehensive valuation reports in under 30 seconds - 50x faster than traditional analysis methods.',
      color: theme.palette.secondary.main,
      gradient: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
    },
    {
      icon: BusinessCenter,
      title: 'Portfolio Management',
      description: 'Track multiple properties, monitor performance metrics, and generate investor-ready reports that demonstrate clear value creation.',
      color: theme.palette.error.main,
      gradient: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
    },
  ];

  const benefits = [
    'Save 80% of time on property analysis with automated valuation reports',
    'Increase investment accuracy by 35% using AI-powered market insights',
    'Access exclusive market data from 50+ sources including STR and CBRE',
    'Get real-time alerts on market shifts and investment opportunities',
    'Generate investor-ready reports with professional charts and analysis',
    'Receive expert support from hotel investment professionals and analysts'
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Navigation Header */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Valuate
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                onClick={toggleTheme}
                sx={{
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{
                  borderRadius: 2,
                  borderColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.5) : theme.palette.primary.main,
                  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.dark,
                    backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.1) : alpha(theme.palette.primary.main, 0.1)
                  }
                }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                }}
              >
                Get Started
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: { xs: 12, md: 16 },
          mt: 8,
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 80%, ${alpha('#ffffff', 0.1)} 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, ${alpha('#ffffff', 0.1)} 0%, transparent 50%)`,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <Stack spacing={4}>
                  <Box>
                    <Chip
                      label="AI-Powered Hotel Valuation"
                      sx={{
                        backgroundColor: alpha('#ffffff', 0.2),
                        color: 'white',
                        fontWeight: 600,
                        mb: 3,
                        border: `1px solid ${alpha('#ffffff', 0.3)}`,
                        px: 1,
                        py: 0.5,
                      }}
                    />
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 800,
                        mb: 2,
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                        lineHeight: 1.1,
                      }}
                    >
                      Transform Your Hotel Investment Decisions
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        opacity: 0.9,
                        fontWeight: 400,
                        lineHeight: 1.4,
                        maxWidth: 600,
                        mb: 3,
                      }}
                    >
                      Leverage cutting-edge AI and comprehensive market data to make informed hotel investment decisions with confidence.
                    </Typography>

                    {/* Trust Indicators - Moved earlier for impact */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} sx={{ mb: 2 }}>
                      <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          $2.5B+
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Assets Valued
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          98%
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Accuracy Rate
                        </Typography>
                      </Box>

                    </Stack>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/signup')}
                      endIcon={<ArrowForward />}
                      sx={{
                        background: `linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)`,
                        color: theme.palette.primary.main,
                        fontWeight: 800,
                        px: 5,
                        py: 2,
                        fontSize: '1.15rem',
                        borderRadius: 3,
                        border: `3px solid #ffffff`,
                        boxShadow: '0 8px 32px rgba(255,255,255,0.4), 0 4px 12px rgba(0,0,0,0.1)',
                        textTransform: 'none',
                        minHeight: 56,
                        '&:hover': {
                          background: `linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)`,
                          transform: 'translateY(-3px) scale(1.02)',
                          boxShadow: '0 12px 40px rgba(255,255,255,0.5), 0 8px 20px rgba(0,0,0,0.15)',
                          border: `3px solid rgba(255,255,255,0.9)`,
                        },
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& .MuiButton-endIcon': {
                          transition: 'transform 0.3s ease',
                        },
                        '&:hover .MuiButton-endIcon': {
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PlayArrow />}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        borderRadius: 3,
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      Watch Demo
                    </Button>
                  </Stack>


                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                {/* Enhanced Dashboard Preview - Professional mockup */}
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 320, md: 520 },
                    background: `linear-gradient(135deg, ${alpha('#ffffff', 0.18)} 0%, ${alpha('#ffffff', 0.08)} 100%)`,
                    borderRadius: 4,
                    border: `3px solid ${alpha('#ffffff', 0.3)}`,
                    backdropFilter: 'blur(20px)',
                    cursor: 'pointer',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.03)',
                      boxShadow: `0 35px 70px ${alpha('#000000', 0.4)}, inset 0 1px 0 rgba(255,255,255,0.4)`,
                      border: `3px solid ${alpha('#ffffff', 0.5)}`,
                      '& .dashboard-elements': {
                        transform: 'scale(1.05)',
                      },
                      '& .pulse-dot': {
                        animationDuration: '1s',
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        radial-gradient(circle at 25% 25%, ${alpha('#60a5fa', 0.15)} 0%, transparent 50%),
                        radial-gradient(circle at 75% 75%, ${alpha('#34d399', 0.12)} 0%, transparent 50%)
                      `,
                      pointerEvents: 'none',
                    }
                  }}
                  onClick={() => navigate('/signup')}
                >
                  <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 4
                  }}>
                    {/* Mock Dashboard Interface */}
                    <Box
                      className="dashboard-elements"
                      sx={{
                        transition: 'transform 0.3s ease',
                        width: '100%',
                        maxWidth: 280
                      }}
                    >
                      {/* Header with title and live indicator */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                        px: 2
                      }}>
                        <Typography variant="h6" sx={{
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '1rem'
                        }}>
                          Hotel Analytics
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            className="pulse-dot"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#34d399',
                              mr: 1,
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                                '100%': { opacity: 1 },
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{
                            color: alpha('#ffffff', 0.9),
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            LIVE
                          </Typography>
                        </Box>
                      </Box>

                      {/* Key Metrics Cards */}
                      <Grid container spacing={1.5} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                          <Box sx={{
                            background: alpha('#ffffff', 0.15),
                            borderRadius: 2,
                            p: 1.5,
                            border: `1px solid ${alpha('#ffffff', 0.2)}`
                          }}>
                            <Typography variant="caption" sx={{
                              color: alpha('#ffffff', 0.8),
                              fontSize: '0.7rem',
                              display: 'block'
                            }}>
                              Valuation
                            </Typography>
                            <Typography variant="h6" sx={{
                              color: '#ffffff',
                              fontWeight: 700,
                              fontSize: '1rem'
                            }}>
                              $24.5M
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{
                            background: alpha('#ffffff', 0.15),
                            borderRadius: 2,
                            p: 1.5,
                            border: `1px solid ${alpha('#ffffff', 0.2)}`
                          }}>
                            <Typography variant="caption" sx={{
                              color: alpha('#ffffff', 0.8),
                              fontSize: '0.7rem',
                              display: 'block'
                            }}>
                              ROI Potential
                            </Typography>
                            <Typography variant="h6" sx={{
                              color: '#34d399',
                              fontWeight: 700,
                              fontSize: '1rem'
                            }}>
                              +32%
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Chart visualization */}
                      <Box sx={{
                        background: alpha('#ffffff', 0.1),
                        borderRadius: 2,
                        p: 2,
                        border: `1px solid ${alpha('#ffffff', 0.15)}`,
                        mb: 3
                      }}>
                        <Typography variant="caption" sx={{
                          color: alpha('#ffffff', 0.8),
                          fontSize: '0.7rem',
                          display: 'block',
                          mb: 1
                        }}>
                          Revenue Projection
                        </Typography>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'end',
                          justifyContent: 'space-between',
                          height: 40,
                          gap: 1
                        }}>
                          {[65, 45, 80, 55, 90, 70, 85].map((height, index) => (
                            <Box key={index} sx={{
                              width: 12,
                              height: `${height}%`,
                              background: `linear-gradient(to top, ${alpha('#60a5fa', 0.8)}, ${alpha('#34d399', 0.6)})`,
                              borderRadius: 1,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scaleY(1.1)'
                              }
                            }} />
                          ))}
                        </Box>
                      </Box>

                      {/* CTA */}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ffffff',
                            fontWeight: 600,
                            mb: 1,
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            fontSize: '0.9rem'
                          }}
                        >
                          See Your Properties Analyzed
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: alpha('#ffffff', 0.9),
                            fontWeight: 500,
                            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                            fontSize: '0.75rem'
                          }}
                        >
                          Click to explore the platform →
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="Trusted by 500+ Investment Professionals"
            sx={{
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.main,
              fontWeight: 600,
              mb: 3,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            The Complete Hotel Investment Platform
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}>
            From property discovery to final acquisition - accelerate every step of your investment process with AI-powered insights and institutional-grade analytics.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Fade in timeout={800 + index * 200}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 40px ${alpha(feature.color, 0.2)}`,
                      borderColor: alpha(feature.color, 0.4),
                      '& .feature-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                      '&::before': {
                        opacity: 1,
                        transform: 'translateY(0)',
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: feature.gradient,
                      opacity: 0.7,
                      transform: 'translateY(-4px)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 3,
                        background: feature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 8px 20px ${alpha(feature.color, 0.25)}`,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          borderRadius: 3,
                          background: 'linear-gradient(45deg, rgba(255,255,255,0.2), transparent)',
                          opacity: 0.8,
                        }
                      }}
                    >
                      <feature.icon sx={{
                        fontSize: 34,
                        color: '#ffffff',
                        zIndex: 1,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                      }} />
                    </Box>
                    <Typography variant="h6" sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: theme.palette.text.primary,
                      fontSize: '1.1rem'
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      lineHeight: 1.7,
                      fontSize: '0.9rem'
                    }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Testimonials Section */}
      <Box sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.5), py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Trusted by Industry Leaders
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              See how hotel investors are transforming their acquisition strategies with Valuate.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 15px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                    borderColor: alpha(theme.palette.primary.main, 0.25),
                  }
                }}
              >
                <FormatQuote
                  sx={{
                    fontSize: 48,
                    color: alpha(theme.palette.primary.main, 0.15),
                    position: 'absolute',
                    top: 12,
                    right: 16,
                    transform: 'rotate(15deg)'
                  }}
                />

                {/* Five star rating */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarOutline key={star} sx={{
                      fontSize: 20,
                      color: theme.palette.warning.main,
                      mr: 0.5
                    }} />
                  ))}
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: 'italic',
                    lineHeight: 1.7,
                    mb: 4,
                    fontSize: '1.05rem',
                    color: theme.palette.text.primary,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  "Valuate transformed our acquisition strategy. The AI insights helped us identify undervalued properties generating 40% higher returns than traditional methods. It's like having a team of expert analysts working 24/7."
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Professional avatar placeholder */}
                  <Box sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    color: 'white'
                  }}>
                    SC
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Sarah Chen
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      Investment Director, Premier Hospitality Group
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 15px 30px ${alpha(theme.palette.success.main, 0.12)}`,
                    borderColor: alpha(theme.palette.success.main, 0.25),
                  }
                }}
              >
                <FormatQuote
                  sx={{
                    fontSize: 48,
                    color: alpha(theme.palette.success.main, 0.15),
                    position: 'absolute',
                    top: 12,
                    right: 16,
                    transform: 'rotate(15deg)'
                  }}
                />

                {/* Five star rating */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarOutline key={star} sx={{
                      fontSize: 20,
                      color: theme.palette.warning.main,
                      mr: 0.5
                    }} />
                  ))}
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: 'italic',
                    lineHeight: 1.7,
                    mb: 4,
                    fontSize: '1.05rem',
                    color: theme.palette.text.primary,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  "The speed and accuracy is incredible. What took our team weeks now takes minutes. We've evaluated 3x more deals this quarter and our hit rate has improved dramatically. Game-changing technology."
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Professional avatar placeholder */}
                  <Box sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    color: 'white'
                  }}>
                    MR
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Michael Rodriguez
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      Managing Partner, Atlantic Real Estate Fund
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Social Proof Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Trusted by leading investment firms worldwide
          </Typography>
          <Grid container spacing={4} sx={{ alignItems: 'center', justifyContent: 'center' }}>
            <Grid item xs={6} md={3}>
              <Box sx={{
                p: 3,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                  $2.5B+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Properties Analyzed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{
                p: 3,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 1 }}>
                  98.5%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Accuracy Rate
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6} md={3}>
              <Box sx={{
                p: 3,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main, mb: 1 }}>
                  24/7
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Market Monitoring
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.02), py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Why Choose Valuate?
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                Join thousands of hotel investors who trust Valuate for their investment decisions.
              </Typography>

              <Stack spacing={2}>
                {benefits.map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle sx={{ color: theme.palette.success.main, mr: 2 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                  Ready to Get Started?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                  Start your free trial today and see how Valuate can transform your hotel investment strategy.
                </Typography>
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => navigate('/signup')}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.5)}`,
                      },
                    }}
                  >
                    Start Free Trial
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={() => navigate('/login')}
                    sx={{ py: 1.5, borderRadius: 2 }}
                  >
                    Sign In
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[800],
          color: 'white',
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Valuate
              </Typography>
              <Typography variant="body2" sx={{
                color: '#475569',
                mb: 2
              }}>
                Transform your hotel investment decisions with AI-powered insights and comprehensive market analysis.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Security sx={{
                  fontSize: 16,
                  mr: 1,
                  color: '#475569'
                }} />
                <Typography variant="body2" sx={{
                  color: '#475569'
                }}>
                  Enterprise-grade security & data protection
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <Box>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#ffffff'
                  }}>
                    Company
                  </Typography>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      About Us
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Contact Us
                    </Typography>

                  </Stack>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#ffffff'
                  }}>
                    Legal
                  </Typography>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Privacy Policy
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Terms of Service
                    </Typography>

                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center', pt: 4, borderTop: `1px solid ${alpha('#ffffff', 0.1)}` }}>
            <Typography variant="body2" sx={{
              color: '#475569'
            }}>
              © 2024 Valuate. All rights reserved. Transform your hotel investment decisions with AI.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
